import { useTranslations } from '@vocab/react';
import {
  Box,
  OverflowMenu,
  MenuItem,
  Text,
  Inline,
  MenuItemLink,
  TextLink,
} from 'braid-design-system';
import React, { useState, useCallback, type FunctionComponent } from 'react';

import { events, link } from 'src/modules/analyticsTracker';
import type { BrandListItem } from 'src/types';
import { getEditUrlForId, getPreviewUrlForId } from 'src/utils/urls';

import DeleteBrandModal from '../../DeleteBrand/DeleteBrandModal';

import translations from './.vocab';

interface Props {
  brand: BrandListItem;
  onToggleDefault: () => void;
}

const BrandControl: FunctionComponent<Props> = ({ brand, onToggleDefault }) => {
  const { id, isLiveOnAd, isDefault } = brand;

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const shouldShowDelete = !isDefault && !isLiveOnAd;

  const onPreviewClick = useCallback(() => {
    link(events.PREVIEW_BRAND_CLICKED);
  }, []);

  const onDeleteClick = useCallback(() => {
    link(events.DELETE_BRAND_CLICKED);
    setShowDeleteModal(true);
  }, []);

  const onCloseModalClick = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  const { t } = useTranslations(translations);
  return (
    <Box display="flex" justifyContent="flexEnd">
      <Inline space="small" alignY="center">
        <Text>
          <TextLink
            href={getEditUrlForId(id)}
            data-testid={`card-edit-${id}`}
            data-analytics={events.EDIT_BRAND_CLICKED}
            onClick={() => {
              try {
                link(events.EDIT_BRAND_CLICKED);
              } catch (e) {
                // eslint-disable-next-line no-console
                console.log('link function not working');
              }
            }}
          >
            {t('Edit')}
          </TextLink>
        </Text>
        <Box>
          <OverflowMenu
            label="brand actions"
            data={{ testid: `card-sub-menu-${id}` }}
          >
            <MenuItemLink
              onClick={onPreviewClick}
              href={getPreviewUrlForId(id)}
              target="_blank"
            >
              {t('Preview')}
            </MenuItemLink>
            <MenuItem onClick={onToggleDefault}>{`${
              isDefault ? t('Clear default') : t('Make default')
            }`}</MenuItem>
            {shouldShowDelete && (
              <MenuItem onClick={onDeleteClick}>{t('Delete')}</MenuItem>
            )}
          </OverflowMenu>
        </Box>
      </Inline>
      <DeleteBrandModal
        isOpen={showDeleteModal}
        onClose={onCloseModalClick}
        brand={brand}
      />
    </Box>
  );
};

export default BrandControl;
